import {
	type IkeFieldTransform,
	type IkeFieldTransformHeight,
	type IkeFieldTransformNumber,
	type IkeFieldTransformSelectList,
	type IkeFieldTransformVector,
} from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import {
	CollectionModelField,
	CollectionModelFieldHeight,
	CollectionModelFieldNumber,
	CollectionModelFieldSelectList,
	CollectionModelFieldVector,
} from '@ikegps/core-shared';

export abstract class FieldTransform<
	K extends IkeFieldTransform,
	T extends CollectionModelField = CollectionModelField,
> {
	fieldId: string;
	transform: K;

	constructor(fieldId: string, transform: K) {
		this.fieldId = fieldId;
		this.transform = transform;
	}

	abstract match(from: T): boolean;

	abstract apply(to: T): void;

	static create<K extends IkeFieldTransform, T extends CollectionModelField>(
		field: T,
		transform: K
	): FieldTransform<K, T> | null {
		const transforms = {
			[IkeFieldType.VECTOR]: {
				invert: FieldTransformVectorInvert,
			},
			[IkeFieldType.NUMBER]: {
				invert: FieldTransformNumberInvert,
			},
			[IkeFieldType.SELECT_LIST]: {
				replace: FieldTransformSelectListReplace,
			},
			[IkeFieldType.HEIGHT]: {
				exclude: FieldTransformHeightExclude,
			},
		};

		// #FIXME Typescript cant determine the type of this variable (due to a bug in the compiler) so its defaulted
		//   to any. Its correct type is something along the lines of "Record<TransformType, FieldTransform<K, T>>"
		const fieldTypeTransforms = transforms[field.schema.type];
		if (!fieldTypeTransforms) {
			return null;
		}

		// #FIXME Typescript cant determine the type of this variable (due to a bug in the compiler) so its defaulted
		const transformClass = fieldTypeTransforms[transform.transform];
		if (!transformClass) {
			return null;
		}

		return new transformClass(field.id, transform);
	}
}

export class FieldTransformVectorInvert extends FieldTransform<IkeFieldTransformVector, CollectionModelFieldVector> {
	match(from: CollectionModelFieldVector): boolean {
		return from.hasValue();
	}

	apply(to: CollectionModelFieldVector): void {
		to.Mutator.reverse();
	}
}

export class FieldTransformNumberInvert extends FieldTransform<IkeFieldTransformNumber, CollectionModelFieldNumber> {
	match(from: CollectionModelFieldNumber): boolean {
		return from.hasValue();
	}

	apply(to: CollectionModelFieldNumber): void {
		to.Mutator.reverse();
	}
}

export class FieldTransformSelectListReplace extends FieldTransform<
	IkeFieldTransformSelectList,
	CollectionModelFieldSelectList
> {
	match(from: CollectionModelFieldSelectList): boolean {
		const toValue = this.transform.replace[from.value];
		return toValue != null;
	}

	apply(to: CollectionModelFieldSelectList): void {
		const toValue = this.transform.replace[to.value] ?? null;
		to.Mutator.manual(toValue);
	}
}

export class FieldTransformHeightExclude extends FieldTransform<IkeFieldTransformHeight, CollectionModelFieldHeight> {
	apply(to: CollectionModelFieldHeight): void {
		to.Mutator.manual(null);
	}

	override match(_from: CollectionModelFieldHeight): boolean {
		return true;
	}
}
